import React, { useState, useEffect } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { getAbsoluteOgUrl, getTwitterUsername } from 'utils/seo'
import { getDisplayUserName } from 'utils/strings'

// next component
import Head from 'next/head'

//components
import SocialShareModal from 'components/Menu/SocialShareModal'
import CreatorMerchShop from './shop'
import ArtistLayout from 'components/Layout/ArtistLayout'
import ProfileMerchReview from 'components/pages/ArtistProfile/ProfileMerchReview'
import Alert from 'components/Alert'

// images
import DefaultAvatar from 'assets/images/dashboard-avatar.png'
import PreviewImage from 'assets/images/studio-preview-image-user.png'
import TwitterWhiteIcon from 'assets/icons/twitter-white-15x15.svg?component'
import DripIcon from 'assets/icons/drip-icon-15x15.svg?component'
import WebsiteIcon from 'assets/icons/website-icon-15x15.svg?component'
import IGWhiteIcon from 'assets/icons/instagram-white-15x15.svg?component'
import LandIcon from 'assets/icons/land-icon-15x15.svg?component'
import SolrPlexIcon from 'assets/icons/solrplex-white-icon.svg?component'
import ThreeDotIcon from 'assets/icons/three-dot-icon.svg?component'

// styles
import styles from './index.module.scss'
import MenuShareMerch from 'components/Menu/MenuShareMerch'
import { ensureHttps } from 'utils/helpers'
import MerchMenuModal from 'components/Modal/MerchMenuModal'
import EditMerchModal from 'components/Modal/EditMerchModal'

interface CreatorMerchProps {
  storeInfoData?: any
  publicArtistData?: any
  error?: string
}

const CreatorMerch = ({ storeInfoData, publicArtistData }: CreatorMerchProps) => {
  const [artistInfo, setArtistInfo] = useState<any>(null)
  const [showMenuModal, setshowMenuModal] = useState(false)
  const [storeMerchInfo, setStoreMerchInfo] = useState<any>(storeInfoData)

  useEffect(() => {
    setArtistInfo(publicArtistData)

    let list = []
    if (publicArtistData?.twitter_url?.length > 0) list.push({ id: list.length, label: 'Twitter', type: 'twitter' })
    if (publicArtistData?.drip_url?.length > 0) list.push({ id: list.length, label: 'DRiP', type: 'drip' })
    if (publicArtistData?.ig_url?.length > 0) list.push({ id: list.length, label: 'Instagram', type: 'instagram' })
    if (publicArtistData?.site_url?.length > 0) list.push({ id: list.length, label: 'Website', type: 'website' })
    if (publicArtistData?.three_land_url?.length > 0) list.push({ id: list.length, label: '3.Land', type: 'land' })
    if (publicArtistData?.solarplex_url?.length > 0) list.push({ id: list.length, label: 'Solarplex', type: 'solar' })
    setWalletList(list)
  }, [publicArtistData])

  const { publicKey } = useWallet()
  const [showEditMerch, setShowEditMerch] = useState(false)
  const [openShareModal, setOpenShareModal] = useState(false)
  const [data, setData] = useState<any[]>([])
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [walletList, setWalletList] = useState([])

  let username = publicArtistData?.username ?? '<not found>'
  let pageTitle =
    storeInfoData?.name?.length > 0 ? storeInfoData.name : `The Official Merch Store of ${username} | Foster Studio`
  let pageImageUrl = storeInfoData?.assetUrl?.length > 0 ? storeInfoData.assetUrl : getAbsoluteOgUrl(PreviewImage.src)

  let pageDescription =
    storeInfoData?.description?.length > 0
      ? storeInfoData.description
      : `Shop limited-edition apparel and collectibles designed by ${username}.`
  let userTwitterHandle = getTwitterUsername(publicArtistData?.twitter_url)

  const [descCount, setDescCount] = useState(115)
  const [tempNftDescription, setTempNftDescription] = useState(storeInfoData ? storeInfoData.description : '')
  const [isTruncated, setIsTruncated] = useState(true)

  const processedNftDescription = isTruncated ? tempNftDescription?.slice(0, descCount) : tempNftDescription
  const shouldShowReadMore = tempNftDescription?.length > descCount

  return (
    <div className="w-full pb-[60px]">
      <Head>
        <title>{pageTitle}</title>
        <meta property="description" content={pageDescription} />

        <meta name="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta name="og:image" content={pageImageUrl} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={pageImageUrl} />
        <meta name="twitter:site" content="@silkrunway" />
        {userTwitterHandle ? <meta name="twitter:creator" content={userTwitterHandle} /> : null}

        <link rel="icon" href="/logo.png" />
      </Head>

      <div className="w-full h-[auto] sm:h-[344px] relative">
        <div className={styles.overlayBG}>
          <div className="g-container pt-[30px] lg:pt-[57px] flex justify-between items-start">
            <div>
              <div className="w-full flex gap-[22px]">
                <div className={styles.profileImage}>
                  <img
                    src={artistInfo?.profile_picture?.length > 0 ? artistInfo?.profile_picture : DefaultAvatar.src}
                    alt=""
                    className="w-[90px] h-[90px] object-cover"
                  />
                </div>
                <div>
                  <div className={styles.merchBy}>Merch by</div>
                  <div className={styles.userName}>{getDisplayUserName(artistInfo?.username)}</div>
                  <div className="flex mt-[16px]">
                    <ProfileMerchReview userId={artistInfo?.id} username={artistInfo?.username} />
                  </div>
                </div>
              </div>
              <div className="flex gap-[15px] z-10 items-center mt-[18px] lg:mt-[27px]">
                {walletList.map((item, index) => (
                  <div
                    key={index}
                    className={styles.shareIcon}
                    onClick={() => {
                      if (item.type === 'twitter') {
                        window.open(ensureHttps(artistInfo?.twitter_url))
                      } else if (item.type === 'drip') {
                        window.open(ensureHttps(artistInfo?.drip_url))
                      } else if (item.type === 'instagram') {
                        window.open(ensureHttps(artistInfo?.ig_url))
                      } else if (item.type === 'website') {
                        window.open(ensureHttps(artistInfo?.site_url))
                      } else if (item.type === 'land') {
                        window.open(ensureHttps(artistInfo?.three_land_url))
                      } else if (item.type === 'solar') {
                        window.open(ensureHttps(artistInfo?.solarplex_url))
                      }
                    }}
                  >
                    {item.type === 'twitter' ? (
                      <TwitterWhiteIcon />
                    ) : item.type === 'drip' ? (
                      <DripIcon />
                    ) : item.type === 'instagram' ? (
                      <IGWhiteIcon />
                    ) : item.type === 'website' ? (
                      <WebsiteIcon />
                    ) : item.type === 'land' ? (
                      <LandIcon />
                    ) : item.type === 'solar' ? (
                      <SolrPlexIcon />
                    ) : (
                      <></>
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.storeName}>{storeMerchInfo?.name}</div>

              <div className="lg:block hidden">
                <div className={styles.storeDesc}>{storeMerchInfo?.description}</div>
              </div>
              <div className="lg:hidden block">
                <div className={styles.storeDesc}>
                  {processedNftDescription}
                  {shouldShowReadMore && (
                    <span
                      onClick={() => {
                        setIsTruncated(!isTruncated)
                      }}
                      className={`${styles.greenText} min-w-max cursor-pointer ml-[4px]`}
                    >
                      {isTruncated ? 'Read More' : ' Collapse'}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="hidden items-center lg:flex">
              <MenuShareMerch
                newProductURL={`/${artistInfo?.username}/merch/new`}
                // merchUrl={`/${artistInfo?.username}/merch`}
                isCreatedMerch={publicKey?.toBase58() === artistInfo?.wallet_id}
                setOpenShareModal={setOpenShareModal}
                editStoreSettings={() => {
                  setShowEditMerch(true)
                }}
              />
            </div>
            <div className="lg:hidden cursor-pointer" onClick={() => setshowMenuModal(true)}>
              <ThreeDotIcon />
            </div>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center">
        <div className="g-container">
          <div className="w-full mt-[30px] lg:mt-[17px] pb-[150px]">
            {artistInfo && (
              <CreatorMerchShop
                username={artistInfo?.username}
                userId={artistInfo?.id}
                userAvatar={artistInfo?.profile_picture?.length > 0 ? artistInfo?.profile_picture : DefaultAvatar.src}
                isCreator={publicKey?.toBase58() === artistInfo?.wallet_id}
              />
            )}
          </div>
        </div>
      </div>
      {openShareModal && (
        <SocialShareModal
          open={openShareModal}
          close={() => setOpenShareModal(false)}
          vote={true}
          message={'Check out this artist on Foster Marketplace @foster_protocol #fostermarketplace'}
          url={`/${artistInfo?.username}/merch`}
        />
      )}

      {showMenuModal && (
        <MerchMenuModal
          open={showMenuModal}
          onClose={() => setshowMenuModal(false)}
          newProductURL={`/${artistInfo?.username}/merch/new`}
          // merchUrl={`/${artistInfo?.username}/merch`}
          isCreatedMerch={publicKey?.toBase58() === artistInfo?.wallet_id}
          setOpenShareModal={setOpenShareModal}
          editStoreSettings={() => {
            setShowEditMerch(true)
          }}
        />
      )}

      <Alert
        open={showAlert}
        close={() => {
          setShowAlert(false)
        }}
        data={data}
      />

      {showEditMerch && (
        <EditMerchModal
          open={showEditMerch}
          close={() => setShowEditMerch(false)}
          onSave={info => {
            setShowEditMerch(false)
            setStoreMerchInfo(info)
          }}
        />
      )}
    </div>
  )
}

export default CreatorMerch

CreatorMerch.getLayout = function getLayout(page) {
  return <ArtistLayout>{page}</ArtistLayout>
}

CreatorMerch.getInitialProps = async function ({ res, query }): Promise<CreatorMerchProps> {
  let artistUsername = encodeURIComponent(query.id)
  try {
    let publicArtistData = await fetch(`${process.env.BACKEND_URL}v1/users/username/${artistUsername}`).then(r =>
      r.json()
    )
    let storeInfoData = await fetch(`${process.env.BACKEND_URL}v1/merch_stores/${publicArtistData.id}`).then(r =>
      r.json()
    )
    if (storeInfoData.hasOwnProperty('message')) storeInfoData = null

    return {
      storeInfoData,
      publicArtistData,
    }
  } catch (e: any) {
    console.error(e)
    return {
      error: `could not load artist details: ${artistUsername}: ${e.message}`,
    }
  }
}
